import { contactPipelineInput } from 'app/lib/contactPipelineInput'
/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { NavArrowDown } from 'iconoir-react'
import { compact } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AddPipelineMenu } from 'app/components/contacts/AddPipelineMenu'
import { PipelineStageDropdown } from 'app/components/contacts/PipelineStageDropdown'
import {
  ACTION_CENTER_FLAG,
  isAssignablePipeline,
  isSystemPipelineId,
} from 'app/components/action-center/systemPipelines'
import { inlineAddChipClasses } from 'app/components/shared/InlineAddActionLink'
import { OverflowCountChip } from 'app/components/shared/OverflowCountChip'
import { EDITABLE_CELL_HOVER, editableChipTrigger } from 'app/components/table/cellInteraction'
import { ChipMeasureRow } from 'app/components/shared/ChipMeasureRow'
import { CHIP_OVERFLOW_ITEM, useChipOverflow } from 'app/hooks/useChipOverflow'
import { useActiveLeadMembership } from 'app/hooks/useActiveLeadMembership'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { usePracticeLists } from 'app/hooks/usePracticeLists'
import { useStatusListMemberships } from 'app/hooks/useStatusListMemberships'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore, useListMembershipStore, usePipelineStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { isCompanionPipeline, visibleMemberships } from 'lib/companionPipelines'
import { membershipEntries, practiceListCatalog, stageIdForName } from 'lib/listMemberships'
import { logError } from 'lib/logging'
import { isFilterList } from 'lib/practiceLists'

// The dynamic stage dot colors (bg-${stage.color}, e.g. bg-pipelines-green) are
// generated by the `@source inline(...)` directive in src/styles/tailwind.css,
// not safelisted here. Update that file if colors are added. A stage with no
// color falls back to the bg-surface-strong role, not a raw palette class.

const lockedChip = `inline-flex h-5 shrink-0 items-center gap-1 rounded border-[0.5px] border-default bg-surface px-1.5 text-label-xs`

type PipelineEntry = { id: string; stage?: string }
type ShownEntry = PipelineEntry & { practice?: boolean; status?: boolean; readOnly?: boolean }

export const ContactPipelinesCell = observer(function ContactPipelinesCell({ contact }: { contact: ContactJson }) {
  const pipelineStore = usePipelineStore()
  const contactStore = useContactStore()
  const membershipStore = useListMembershipStore()
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const groupedNav = useFeatureToggle(`grouped-navigation`)
  const { lists } = usePracticeLists({ enabled: !!groupedNav })
  const status = useStatusListMemberships(groupedNav ? contact : undefined)
  const activeLead = useActiveLeadMembership(groupedNav ? contact : undefined)
  const copy = useListCopy()
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [optimisticPipelines, setOptimisticPipelines] = useState<PipelineEntry[] | undefined>()

  useEffect(() => {
    if (!pipelineStore.list && !pipelineStore.listLoading) {
      pipelineStore.fetchList()
    }
  }, [pipelineStore])

  // A table row cannot afford its own read: the store batches every row of a page into one.
  useEffect(() => {
    if (groupedNav && contact.id) membershipStore.request(contact.id)
  }, [groupedNav, contact.id, membershipStore])

  // Drop the optimistic value once the store-backed prop catches up.
  useEffect(() => setOptimisticPipelines(undefined), [contact.pipelines])

  const practiceCatalog = useMemo(() => (groupedNav ? practiceListCatalog(lists) : []), [groupedNav, lists])

  // Index pipelines by id once so per-row, per-pipeline resolution is O(1) rather than a scan.
  // Ungated on purpose: the store always carries the system pipelines, so a contact
  // enrolled in one still renders its name, colour and stage rather than a bare label,
  // whatever the toggle says. Only the add menu below is gated.
  const pipelinesById = useMemo(
    () =>
      new Map(
        [...pipelineStore.hits, ...practiceCatalog, ...status.catalog, ...activeLead.catalog].map((p) => [p.id, p]),
      ),
    [pipelineStore.hits, practiceCatalog, status.catalog, activeLead.catalog],
  )
  const listsById = useMemo(() => new Map(lists.map((list) => [list.id, list])), [lists])

  const pipelines = useMemo<PipelineEntry[]>(
    () => compact(optimisticPipelines ?? contact.pipelines ?? []) as PipelineEntry[],
    [optimisticPipelines, contact.pipelines],
  )
  // What the cell draws, counts and overflows. Writes still send `pipelines` whole. With the grouped
  // navigation on, the record only supplies system lists: a practice list is the store's, so an old
  // pipeline id that is now a list id is never drawn twice.
  const recordShown = useMemo(
    () => visibleMemberships(pipelines, pipelineStore.hits, { hideSystem: false }),
    [pipelines, pipelineStore.hits],
  )
  const practiceShown = groupedNav ? membershipEntries(membershipStore.membershipsOf(contact.id!), lists) : []
  const shown: ShownEntry[] = groupedNav
    ? [
        ...recordShown.filter(
          (p) => isSystemPipelineId(p.id) && !(p.id in status.replacing) && p.id !== activeLead.replacing,
        ),
        ...(activeLead.entry ? [{ ...activeLead.entry, readOnly: true }] : []),
        ...status.entries.map((entry) => ({ ...entry, status: true })),
        ...practiceShown.map((entry) => ({ ...entry, practice: true })),
      ]
    : recordShown

  const practiceOnIds = practiceShown.map((entry) => entry.id).join(`,`)
  // Before this row's read lands a list it is already on looks joinable, so no list is offered until then.
  const membershipsLoaded = groupedNav && membershipStore.isLoaded(contact.id!)

  // A workspace without the Action Center is not offered its pipelines: the board that
  // works them is behind `FeatureRoute`, so a membership added here would be one nobody
  // could display or clear.
  const availablePipelines = useMemo(() => {
    if (groupedNav) {
      if (!membershipsLoaded) return []
      const onIds = new Set(practiceOnIds.split(`,`))
      return practiceCatalog.filter((entry) => !onIds.has(entry.id) && !isFilterList(listsById.get(entry.id)!))
    }
    const memberIds = new Set(pipelines.map((p) => p.id))
    // Wrapped, not passed by reference: `isAssignablePipeline` takes an optional
    // contact as its second argument and `filter` would hand it the index.
    const offerable = actionCenterEnabled
      ? pipelineStore.hits
      : pipelineStore.hits.filter((pipeline) => isAssignablePipeline(pipeline))
    return offerable.filter((p) => p.id && !memberIds.has(p.id)).filter((pipeline) => !isCompanionPipeline(pipeline))
  }, [
    pipelineStore.hits,
    pipelines,
    actionCenterEnabled,
    groupedNav,
    membershipsLoaded,
    practiceCatalog,
    listsById,
    practiceOnIds,
  ])

  // Optimistic full-array write with rollback — same shape as the drawer/inbox
  // pipelines editors. Input is mapped to { id, stage } explicitly: entries off the
  // row carry urql's __typename, which UpdateContactInput.pipelines rejects.
  const persist = async (next: PipelineEntry[], onSuccess?: () => void) => {
    const input = contactPipelineInput(next)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }

    setOptimisticPipelines(next)
    const result = await contactStore.update(contact.id!, {
      pipelines: input,
    })
    if (result.error) {
      setOptimisticPipelines(undefined)
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
    } else {
      onSuccess?.()
    }
  }

  const changeStage = (pipelineId: string, stage: string) =>
    persist(pipelines.map((p) => (p.id === pipelineId ? { ...p, stage } : p)))

  const removePipeline = (pipelineId: string) => persist(pipelines.filter((p) => p.id !== pipelineId))

  const addPipeline = (pipelineId: string, stage: string) => {
    const next = [...pipelines.map((p) => ({ id: p.id, stage: p.stage })), { id: pipelineId, stage }]
    persist(next, () => {
      notifySuccess(copy.contactAddedToList)
      // Board ES-lag workaround: the board merges pending contacts until the index catches up.
      contactStore.addPendingPipelineContact(pipelineId, { ...contact, pipelines: next } as ContactJson)
    })
  }

  const changePracticeStage = async (listId: string, stage: string) => {
    const stageId = stageIdForName(listsById.get(listId)!, stage)
    if (!stageId) return
    try {
      await membershipStore.move(listId, contact, stageId)
    } catch (error) {
      logError(`Moving a contact between list stages failed`, error)
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
    }
  }

  const removePracticeList = async (listId: string) => {
    try {
      await membershipStore.remove(listId, contact)
    } catch (error) {
      logError(`Removing a contact from a list failed`, error)
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
    }
  }

  const addPracticeList = async (listId: string, stage: string) => {
    const stageId = stageIdForName(listsById.get(listId)!, stage)
    if (!stageId) return
    try {
      const { added } = await membershipStore.add(listId, [contact], stageId)
      if (added.length > 0) notifySuccess(copy.contactAddedToList)
      else notifyError(copy.errorAddingContactToList, `pipeline-add-error`)
    } catch (error) {
      logError(`Adding a contact to a list failed`, error)
      notifyError(copy.errorAddingContactToList, `pipeline-add-error`)
    }
  }

  const onAdd = groupedNav ? addPracticeList : addPipeline

  // Shared measurement-based clipping (fit whole chips, "+N" the rest).
  // Reserve room for the always-present "+" add chip next to the "+N" badge.
  const { visibleCount } = useChipOverflow({ containerRef, measureRef, itemCount: shown.length, reservedWidth: 30 })

  if (shown.length === 0) {
    return (
      // Clips like the filled branch below: the verb expands on cell hover and
      // must not paint over the cell border. The menu itself is portaled.
      <div className="flex min-w-0 items-center overflow-hidden px-2">
        <AddPipelineMenu
          pipelines={availablePipelines}
          onAdd={onAdd}
          triggerClassName={`${inlineAddChipClasses} ${EDITABLE_CELL_HOVER} min-w-0 text-left focus:outline-none`}
          triggerAriaLabel={copy.addToList}
        >
          <span>+</span>
          <span className="hidden min-w-0 truncate group-hover/cell:inline">&nbsp;{copy.addToList}</span>
        </AddPipelineMenu>
      </div>
    )
  }

  const hidden = shown.slice(visibleCount)

  // The chip's inner content, shared by the live dropdown trigger and the hidden
  // measure copy so their widths can never drift apart.
  const chipContent = (cp: PipelineEntry) => {
    const pipeline = pipelinesById.get(cp.id)
    const stage = pipeline?.stages?.find((s) => s.name === cp.stage)
    const color = stage?.color || `surface-strong`
    return (
      <>
        <span className={`h-2 w-2 shrink-0 rounded-[2px] bg-${color}`} />
        {pipeline?.name && <span className="truncate text-strong">{pipeline.name}</span>}
        {pipeline?.name && cp.stage && <span className="shrink-0 text-lg leading-none text-minimal">·</span>}
        {cp.stage && <span className="truncate text-subtle">{cp.stage}</span>}
        {/* Constant-width chevron (opacity toggle, not display) so chips don't reflow on hover. */}
        <NavArrowDown
          className="h-3 w-3 shrink-0 text-minimal opacity-0 transition-opacity group-hover/chip:opacity-100"
          strokeWidth={2}
        />
      </>
    )
  }

  return (
    <div ref={containerRef} className="relative flex h-full w-full items-center gap-1 overflow-hidden px-2">
      {shown.slice(0, visibleCount).map((cp, index) => {
        const pipeline = pipelinesById.get(cp.id)
        // A filter list's membership is the rule's, and a status list's is the record's too, so each has a stage picker and no Remove.
        const removable = !cp.status && (!cp.practice || !isFilterList(listsById.get(cp.id)!))
        const remove = cp.practice ? () => removePracticeList(cp.id) : () => removePipeline(cp.id)
        const change = (s: string) =>
          cp.status ? status.changeStage(cp.id, s) : cp.practice ? changePracticeStage(cp.id, s) : changeStage(cp.id, s)
        const statusEntry = cp.status ? status.catalog.find((entry) => entry.id === cp.id) : undefined
        if (cp.readOnly || (statusEntry?.lockedStage && statusEntry.lockedStage === cp.stage)) {
          // A stage only a booking or the Lead Status column sets: drawn like the other chips, with no menu to open.
          return (
            <span key={`${cp.id}-${cp.stage}-${index}`} className={lockedChip}>
              {chipContent(cp)}
            </span>
          )
        }
        return (
          <PipelineStageDropdown
            key={`${cp.id}-${cp.stage}-${index}`}
            stages={
              statusEntry
                ? statusEntry.stages.filter((stage) => stage.name !== statusEntry.lockedStage)
                : (pipeline?.stages ?? [])
            }
            currentStage={cp.stage ?? ``}
            onChange={change}
            onRemove={removable ? remove : undefined}
            triggerClassName={editableChipTrigger}
          >
            {chipContent(cp)}
          </PipelineStageDropdown>
        )
      })}
      <OverflowCountChip
        items={hidden.map((cp) => [pipelinesById.get(cp.id)?.name, cp.stage].filter(Boolean).join(` · `))}
      />
      {/* Hidden always-complete copy the overflow hook measures. */}
      <ChipMeasureRow measureRef={measureRef}>
        {shown.map((cp, index) => (
          <span key={`${cp.id}-${cp.stage}-${index}`} {...{ [CHIP_OVERFLOW_ITEM]: `` }} className={editableChipTrigger}>
            {chipContent(cp)}
          </span>
        ))}
      </ChipMeasureRow>
      <AddPipelineMenu
        pipelines={availablePipelines}
        onAdd={onAdd}
        triggerClassName={`inline-flex h-5 shrink-0 cursor-pointer items-center rounded border-[0.5px] border-strong bg-surface px-1.5 text-label-xs text-subtle hover:border-(--fg-minimal) hover:text-default focus:outline-none ${EDITABLE_CELL_HOVER}`}
        triggerAriaLabel={copy.addToList}
      >
        +
      </AddPipelineMenu>
    </div>
  )
})
